import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtNum, curSym, fmtDate, errMsg, openPdf, fileUrl } from "@finance/lib/api";
import { useAuth } from "@finance/context/AuthContext";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Switch } from "@finance/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { CurrencySelect } from "@finance/components/CurrencySelect";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Plus, DownloadSimple, CheckCircle, XCircle, Money, Trash, Scan, CircleNotch, UploadSimple, Receipt, Coins, Clock, Paperclip } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";
import { LockedPeriodBanner } from "@finance/components/LockedPeriodBanner";
import { ReverseActions } from "@finance/components/ReverseActions";
import { EfdTimeField } from "@finance/components/EfdTimeField";

const TAX_TYPES = ["Vatable", "Zero-Rated", "Exempt", "Not VATable"];

const STATUS_TABS = ["all", "Pending", "Approved", "Paid", "Rejected"];

const statusStyle = (s) => ({
  Pending: "bg-amber-50 text-amber-700",
  Approved: "bg-blue-50 text-blue-700",
  Paid: "bg-emerald-50 text-emerald-700",
  Rejected: "bg-red-50 text-red-600",
}[s] || "bg-gray-100 text-gray-600");

export default function Expenses() {
  const { user } = useAuth();
  const isApprover = user?.is_executive || ["HoD", "FM"].includes(user?.role_code);
  const canPush = user?.is_owner || user?.can_push_to_payables;
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [newExpenseFile, setNewExpenseFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [pushFor, setPushFor] = useState(null);
  const [pushForm, setPushForm] = useState({ note: "", credit_days: "" });
  const [efdFor, setEfdFor] = useState(null);
  const [efdForm, setEfdForm] = useState({});
  const [efdOcrLoading, setEfdOcrLoading] = useState(false);
  const [popFile, setPopFile] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [viewFor, setViewFor] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [approveForm, setApproveForm] = useState({ account_code: "", supplier_id: "none", note: "", vat_applicable: true, credit_days: "", split_enabled: false, splits: [], settlement_method: "Immediate" });
  const [previewDocId, setPreviewDocId] = useState(null);
  const [bdCats_REMOVED] = useState([]);

  const load = useCallback(() => {
    const params = tab !== "all" ? { status: tab } : {};
    api.get("/expenses", { params }).then((r) => setRows(r.data));
    api.get("/expenses").then((r) => setAllRows(r.data)).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const efdId = searchParams.get("efd");
    if (!efdId || !allRows.length) return;
    const e = allRows.find((x) => x.id === efdId);
    if (e) {
      setEfdFor(e);
      setEfdForm({ efd_applicable: e.efd_applicable !== false, efd_code: e.efd_code || "", efd_time: e.efd_time || "" });
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allRows]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);
  useEffect(() => { api.get("/gl/accounts").then((r) => setAccounts(r.data)).catch(() => {}); }, []);
  useEffect(() => { /* BD expense types removed — all BD posts to a single GL */ }, []);
  useEffect(() => { api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => {}); }, []);

  const confirmPush = async () => {
    if (!pushForm.note.trim()) { toast.error("A note is required to push without EFD"); return; }
    try {
      await api.post(`/expenses/${pushFor.id}/push-to-payables`, {
        note: pushForm.note.trim(),
        credit_days: pushForm.credit_days === "" ? null : Number(pushForm.credit_days),
      });
      toast.success("Pushed into Payables Due");
      setPushFor(null); setPushForm({ note: "", credit_days: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirmReject = async () => {
    try {
      await api.post(`/expenses/${rejectFor.id}/reject`, { note: rejectReason || "Rejected" });
      toast.success("Expense rejected — requestor notified");
      setRejectFor(null); setRejectReason(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openApprove = (e) => {
    let acct = (e.account_code && e.account_code !== "5100") ? e.account_code : "";
    if (e.is_bd) acct = "5100";
    setApproveFor(e);
    setApproveForm({ account_code: acct, supplier_id: e.supplier_id || "none", note: "", vat_applicable: (e.tax_type === "Vatable"), vat_amount: (e.tax_type === "Vatable") ? Number((e.vat_amount != null ? e.vat_amount : Number(e.amount_exclusive || 0) * 0.18).toFixed(2)) : 0, credit_days: e.credit_days ?? "", split_enabled: false, splits: [], settlement_method: e.settlement_method || (e.supplier_id ? "Credit" : "Immediate") });
    setPreviewDocId((e.documents || [])[0]?.id || null);
  };

  const confirmApprove = async () => {
    if (!approveForm.account_code) { toast.error("Select the GL account to debit"); return; }
    const excl = Number(approveFor?.amount_exclusive) || 0;
    let gl_splits = null;
    if (approveForm.split_enabled && approveForm.splits.length) {
      const clean = approveForm.splits.filter((s) => s.account_code && s.amount !== "");
      const sum = clean.reduce((a, s) => a + Number(s.amount || 0), 0);
      if (Math.abs(sum - excl) > 0.01) { toast.error(`Splits must sum to the exclusive amount (${fmtNum(excl)}); got ${fmtNum(sum)}`); return; }
      gl_splits = clean.map((s) => ({ account_code: s.account_code, amount: Number(s.amount) }));
    }
    const changed = ((approveFor.supplier_id || null) && approveForm.supplier_id === "none") ||
                    (!approveFor.is_bd && approveForm.account_code !== (approveFor.account_code || "5100")) ||
                    (approveForm.vat_applicable !== (approveFor.tax_type === "Vatable"));
    if (changed && !(approveForm.note || "").trim()) { toast.error("Add an approval note — you changed the requested GL, VAT or supplier."); return; }
    try {
      await api.post(`/expenses/${approveFor.id}/approve`, {
        account_code: approveForm.account_code,
        supplier_id: approveForm.supplier_id === "none" ? null : approveForm.supplier_id,
        note: approveForm.note || "",
        settlement_method: approveForm.settlement_method,
        vat_applicable: approveForm.vat_applicable,
        vat_amount: approveForm.vat_applicable ? Number(approveForm.vat_amount || 0) : 0,
        credit_days: approveForm.credit_days === "" ? null : Number(approveForm.credit_days),
        gl_splits,
      });
      toast.success("Expense approved & posted");
      setApproveFor(null); setApproveForm({ account_code: "", supplier_id: "none", note: "", vat_applicable: true, credit_days: "", split_enabled: false, splits: [], settlement_method: "Immediate" }); setPreviewDocId(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveEfd = async () => {
    try {
      await api.patch(`/expenses/${efdFor.id}/efd`, { efd_applicable: efdForm.efd_applicable !== false, efd_code: efdForm.efd_code, efd_time: efdForm.efd_time });
      toast.success("EFD details saved");
      setEfdFor(null); setEfdForm({}); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const scanEfd = async (file) => {
    if (!file || !efdFor) return;
    setEfdOcrLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("document_type", "Supplier_Tax_Invoice");
      const { data } = await api.post("/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f = data.fields || {};
      const code = f.efd_code || f.receipt_code || f.verification_code || f.fiscal_code || f.invoice_number || "";
      const time = f.efd_time || f.time || f.date || f.invoice_date || "";
      setEfdForm((s) => ({ ...s, efd_code: code || s.efd_code, efd_time: time || s.efd_time }));
      const fd2 = new FormData(); fd2.append("file", file);
      await api.post(`/expenses/${efdFor.id}/documents`, fd2, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt scanned — EFD fields auto-filled and receipt attached");
    } catch (e) { toast.error(errMsg(e) || "Could not scan receipt"); }
    finally { setEfdOcrLoading(false); }
  };

  const attachEfd = async (file) => {
    if (!file || !efdFor) return;
    setEfdOcrLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/expenses/${efdFor.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/expenses/${efdFor.id}`);
      setEfdFor(data);
      toast.success("EFD copy attached");
    } catch (e) { toast.error(errMsg(e) || "Could not attach EFD copy"); }
    finally { setEfdOcrLoading(false); }
  };

  const excl = Number(form.amount_exclusive) || 0;
  const vat = form.tax_type === "Vatable" ? excl * 0.18 : 0;
  const incl = excl + vat;

  const runReceiptOcr = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", "Expense_Receipt");
      const { data } = await api.post("/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f = data?.fields || {};
      if (Object.keys(f).length === 0) { toast.info("Couldn't read the receipt — please fill the details manually"); return; }
      setForm((s) => {
        const p = { ...s };
        if (f.date && !s.date) p.date = String(f.date).slice(0, 10);
        const amt = Number(f.amount) || 0, vat = Number(f.vat_amount) || 0;
        if (amt && !s.amount_exclusive) p.amount_exclusive = vat ? Math.max(Number((amt - vat).toFixed(2)), 0) : amt;
        if (!s.description) p.description = f.details || f.supplier_name || s.description;
        return p;
      });
      if (f.supplier_name) {
        const q = String(f.supplier_name).toLowerCase().slice(0, 6);
        const match = suppliers.find((x) => (x.name || "").toLowerCase().includes(q));
        if (match) setForm((s) => ({ ...s, supplier_id: (s.supplier_id && s.supplier_id !== "none") ? s.supplier_id : match.id }));
      }
      toast.success("Receipt scanned — review the prefilled details");
    } catch (e) { toast.error("Could not read the receipt"); }
    finally { setOcrLoading(false); }
  };

  const save = async () => {
    if (!form.description) { toast.error("Description is required"); return; }
    if (!excl) { toast.error("Amount is required"); return; }
    try {
      const res = await api.post("/expenses", {
        ...form,
        amount_exclusive: excl,
        account_code: form.account_code || "5100",
        tax_type: form.tax_type || "Vatable",
        currency: form.currency || "TZS",
        fx_rate: Number(form.fx_rate || 1),
        expense_type: form.expense_type || "Expense",
        supplier_id: form.supplier_id === "none" ? null : form.supplier_id,
      });
      if (newExpenseFile && res?.data?.id) {
        try {
          const fd = new FormData(); fd.append("file", newExpenseFile);
          await api.post(`/expenses/${res.data.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) { toast.error("Expense saved, but the attachment failed to upload."); }
      }
      toast.success("Expense submitted for approval");
      setNewExpenseFile(null);
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const act = async (id, action, opts) => {
    try {
      await api.post(`/expenses/${id}/${action}`, opts || {});
      toast.success(`Expense ${action === "pay" ? "marked paid" : action + "d"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense and reverse any GL postings?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Expense deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const issuePo = async (e) => {
    try {
      const { data } = await api.post(`/expenses/${e.id}/issue-po`);
      toast.success(`Purchase order ${data.po_no} issued`);
      openPdf(`pdf/expense-po/${e.id}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const cols = [
    { key: "date", label: "Date", format: fmtDate },
    { key: "description", label: "Description" },
    { key: "supplier_name", label: "Supplier" },
    { key: "account_name", label: "Account" },
    { key: "amount_exclusive", label: "Exclusive", format: (v) => fmtNum(v) },
    { key: "vat_amount", label: "VAT", format: (v) => fmtNum(v) },
    { key: "amount_inclusive", label: "Total", format: (v) => fmtNum(v) },
    { key: "status", label: "Status" },
  ];

  const totalIncl = allRows.reduce((s, e) => s + (e.amount_inclusive || 0), 0);
  const pendingCount = allRows.filter((e) => e.status === "Pending").length;
  const paidCount = allRows.filter((e) => e.status === "Paid").length;

  return (
    <Layout>
      <PageHeader
        title="Expenses"
        subtitle="Request → approve → paid, auto-posted to the general ledger"
        actions={
          <>
            <Button variant="outline" data-testid="export-expenses" onClick={() => exportToCsv("expenses", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-expense-btn" onClick={() => { setForm({ tax_type: "Vatable", account_code: "5100", currency: "TZS", expense_type: "Expense" }); setOpen(true); }}><Plus size={16} className="mr-1" />New Expense</Button>
          </>
        }
      />
      <div className="p-8">
        <KpiStrip cols={4}>
          <KpiCard icon={Receipt} label="Expenses" value={allRows.length} testid="kpi-exp-count" />
          <KpiCard icon={Coins} label="Total Value" value={fmtNum(totalIncl)} tone="accent" testid="kpi-exp-total" />
          <KpiCard icon={Clock} label="Pending" value={pendingCount} tone="warning" testid="kpi-exp-pending" />
          <KpiCard icon={CheckCircle} label="Paid" value={paidCount} tone="success" testid="kpi-exp-paid" />
        </KpiStrip>
        <div className="mb-4 flex gap-2">
          {STATUS_TABS.map((s) => (
            <button key={s} data-testid={`expense-tab-${s}`} onClick={() => setTab(s)}
              className={`rounded-sm px-3 py-1.5 text-sm transition-colors ${tab === s ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <EmptyState icon={Receipt} testid="expenses-empty" title="No expenses" message="Create an expense to start the request → approve → paid workflow." />
        ) : (
        <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-left">Supplier</th>
              <th className="px-4 py-2 text-left">Account</th>
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 lx-num">{fmtDate(e.date)}</td>
                  <td className="px-4 py-2 font-medium">{e.description}<div className="text-xs text-gray-400">{e.is_bd ? "BD" : e.expense_type} · {e.created_by_name}{e.is_bd && (e.trip_no || e.trip_name) ? <span className="ml-1 rounded-sm bg-amber-50 px-1 text-amber-700" data-testid={`exp-trip-tag-${e.id}`}>{e.trip_no || e.trip_name}</span> : null}{(e.status === "Approved" || e.status === "Paid") && (e.efd_status === "not_applicable" ? <span className="ml-1 rounded-sm bg-gray-100 px-1 text-gray-500">EFD N/A</span> : (e.efd_status === "received" || e.efd_code) ? <span className="ml-1 rounded-sm bg-emerald-50 px-1 text-emerald-700">EFD {e.efd_code || "✓"}</span> : e.efd_status === "overdue" ? <span className="ml-1 rounded-sm bg-red-50 px-1 text-red-600">EFD overdue</span> : <span className="ml-1 rounded-sm bg-amber-50 px-1 text-amber-700">EFD awaiting</span>)}</div></td>
                  <td className="px-4 py-2">{e.supplier_name || "—"}</td>
                  <td className="px-4 py-2 text-xs">{e.account_name}</td>
                  <td className="px-4 py-2 text-right lx-num">{curSym(e.currency)} {fmtNum(e.amount_inclusive)}</td>
                  <td className="px-4 py-2"><span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${statusStyle(e.status)}`}>{e.status}</span></td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      <button className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#1B4383] hover:underline" data-testid={`view-expense-${e.id}`} onClick={() => setViewFor(e)}>View</button>
                      {e.status === "Pending" && isApprover && (
                        <>
                          <button className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline" data-testid={`approve-expense-${e.id}`} onClick={() => openApprove(e)}><CheckCircle size={14} />Approve</button>
                          <button className="inline-flex items-center gap-1 text-xs text-red-500 hover:underline" data-testid={`reject-expense-${e.id}`} onClick={() => { setRejectFor(e); setRejectReason(""); }}><XCircle size={14} />Reject</button>
                        </>
                      )}
                      {e.status === "Pending" && !isApprover && <span className="text-xs text-gray-400">Awaiting approval</span>}
                      {(e.status === "Approved" || e.status === "Paid") && (
                        <button className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#1B4383] hover:underline" data-testid={`issue-po-${e.id}`} onClick={() => issuePo(e)}>{e.po_no ? "Print PO" : "Issue PO"}</button>
                      )}
                      {e.status === "Paid" && (
                        <button className="inline-flex items-center gap-1 text-xs text-[#1B4383] hover:underline" data-testid={`voucher-expense-${e.id}`} onClick={() => openPdf(`pdf/payment-voucher/${e.id}`)}><DownloadSimple size={14} />Print Voucher</button>
                      )}
                      {(e.status === "Approved" || e.status === "Paid") && e.efd_status !== "not_applicable" && (
                        <button className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline" data-testid={`efd-expense-${e.id}`} onClick={() => { setEfdFor(e); setEfdForm({ efd_applicable: e.efd_applicable !== false, efd_code: e.efd_code || "", efd_time: e.efd_time || "" }); }}>{e.efd_code ? "Edit EFD" : "Add EFD"}</button>
                      )}
                      {e.status === "Approved" && canPush && e.supplier_name && !e.efd_pushed && e.efd_status !== "received" && e.efd_status !== "not_applicable" && (
                        <button className="inline-flex items-center gap-1 text-xs text-amber-600 hover:underline" data-testid={`push-payables-${e.id}`} onClick={() => { setPushFor(e); setPushForm({ note: "", credit_days: e.credit_days ?? "" }); }}>Push to Payables</button>
                      )}
                      <button className="text-gray-300 hover:text-red-500" data-testid={`delete-expense-${e.id}`} onClick={() => del(e.id)}><Trash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-display">New Expense</DialogTitle></DialogHeader>
          <LockedPeriodBanner date={form.date} className="mb-2" />
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="lx-label">Description</Label>
              <Input className="mt-1" data-testid="expense-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label className="lx-label">Type</Label>
              <Select value={form.expense_type || "Expense"} onValueChange={(v) => setForm({ ...form, expense_type: v })}>
                <SelectTrigger className="mt-1" data-testid="expense-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Expense">Expense</SelectItem><SelectItem value="Purchase">Purchase</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="lx-label">Cost object <span className="text-gray-400">· trip / site / project</span></Label>
              <Input className="mt-1" data-testid="expense-cost-object" placeholder="e.g. Mwanza site, Trip TR-014, Project X" value={form.cost_object || ""} onChange={(e) => setForm({ ...form, cost_object: e.target.value })} />
            </div>
            <div>
              <Label className="lx-label">GL Account</Label>
              <div className="mt-1 flex items-center gap-1">
                <SearchableSelect className="flex-1" testid="expense-account" value={form.account_code || "5100"} placeholder="Select account"
                  onValueChange={(v) => setForm({ ...form, account_code: v })}
                  options={accounts.filter((a) => a.active !== false).map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }))} />
                <QuickAddButton type="account" onCreated={(a) => { api.get("/gl/accounts").then((r) => setAccounts(r.data)); setForm((s) => ({ ...s, account_code: a.code })); }} />
              </div>
            </div>
            <div>
              <Label className="lx-label">Supplier (optional)</Label>
              <div className="mt-1 flex items-center gap-1">
                <SearchableSelect className="flex-1" testid="expense-supplier" value={form.supplier_id || "none"} placeholder="Select supplier"
                  onValueChange={(v) => setForm({ ...form, supplier_id: v })}
                  options={[{ value: "none", label: "— None —" }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))]} />
                <QuickAddButton type="supplier" onCreated={(s) => { api.get("/suppliers").then((r) => setSuppliers(r.data)); setForm((v) => ({ ...v, supplier_id: s.id })); }} />
              </div>
            </div>
            <div>
              <Label className="lx-label">Date</Label>
              <DateField className="mt-1" data-testid="expense-date" value={(form.date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label className="lx-label">Amount (Exclusive)</Label>
              <Input type="number" className="mt-1" data-testid="expense-amount" value={form.amount_exclusive || ""} onChange={(e) => setForm({ ...form, amount_exclusive: e.target.value })} />
            </div>
            <div>
              <Label className="lx-label">Tax Type</Label>
              <Select value={form.tax_type || "Vatable"} onValueChange={(v) => setForm({ ...form, tax_type: v })}>
                <SelectTrigger className="mt-1" data-testid="expense-tax-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TAX_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="lx-label">Currency</Label>
              <CurrencySelect className="mt-1" testid="expense-currency" value={form.currency || "TZS"} onValueChange={(v) => setForm({ ...form, currency: v, fx_rate: v === "TZS" ? 1 : (form.fx_rate || 1) })} />
            </div>
            <div>
              <Label className="lx-label">FX Rate → TZS</Label>
              <Input type="number" className="mt-1" data-testid="expense-fx" disabled={(form.currency || "TZS") === "TZS"} value={form.fx_rate ?? 1} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <Switch checked={!!form.efd_applicable} data-testid="expense-efd-applicable" onCheckedChange={(v) => setForm({ ...form, efd_applicable: v })} /> EFD applicable (verification code &amp; timestamp / copy can be lodged once approved, or at payment)
            </label>
            <div className="col-span-2">
              <Label className="lx-label">Notes</Label>
              <Textarea className="mt-1" rows={2} data-testid="expense-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="lx-label">Attachment (optional)</Label>
              <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-sm border border-dashed border-gray-300 px-3 py-2 text-sm hover:bg-gray-50" data-testid="expense-attach">
                <Paperclip size={15} className="text-gray-400" />
                <span className="truncate text-gray-600">{newExpenseFile ? newExpenseFile.name : "Attach a receipt / invoice (image or PDF)"}</span>
                <input type="file" className="hidden" data-testid="expense-attach-input" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files[0] || null; setNewExpenseFile(file); if (file) runReceiptOcr(file); }} />
              </label>
              {ocrLoading && <p className="mt-1 flex items-center gap-1 text-xs text-[#1B4383]"><CircleNotch size={13} className="animate-spin" />Scanning receipt to prefill amount, date & vendor…</p>}
              {newExpenseFile && !ocrLoading && <button type="button" data-testid="expense-rescan" className="mt-1 inline-flex items-center gap-1 text-xs text-[#1B4383] hover:underline" onClick={() => runReceiptOcr(newExpenseFile)}><Scan size={13} />Re-scan receipt</button>}
            </div>
          </div>
          <div className="flex justify-end gap-8 text-sm">
            <div><span className="lx-label">VAT</span> <span className="lx-num ml-2">{fmtNum(vat)}</span></div>
            <div><span className="lx-label">Total</span> <span className="lx-num ml-2 font-bold text-[#1B4383]">{fmtNum(incl)}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-expense" onClick={save}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!approveFor} onOpenChange={(v) => { if (!v) { setApproveFor(null); setApproveForm({ account_code: "", supplier_id: "none", note: "" }); setPreviewDocId(null); } }}>
        <DialogContent className="max-w-md" data-testid="approve-expense-dialog">
          <DialogHeader><DialogTitle className="font-display">Approve Expense</DialogTitle></DialogHeader>
          {approveFor && (
            <div className="space-y-3">
              <div className="rounded-sm bg-gray-50 p-3 text-sm">
                <div className="font-medium text-gray-800">{approveFor.description}</div>
                <div className="text-xs text-gray-500">{curSym(approveFor.currency || "TZS")} {fmtNum(approveFor.amount_inclusive ?? approveFor.amount_exclusive ?? 0)}{approveFor.is_bd && approveFor.trip_destination ? ` · Trip to ${approveFor.trip_destination}` : ""}</div>
                {approveFor.supplier_memo ? <div className="mt-1 text-xs text-gray-500" data-testid="approve-supplier-memo">HR-suggested supplier (memo): <span className="font-medium text-gray-700">{approveFor.supplier_memo}</span> <span className="text-gray-400">— not a contact yet; pick or onboard below.</span></div> : null}
              </div>
              {approveFor.is_bd ? (
                <div data-testid="approve-bd-gl">
                  <Label className="lx-label">GL account</Label>
                  <div className="mt-1 rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">Business Development — fixed to GL {approveForm.account_code || "5100"} (no selection needed)</div>
                </div>
              ) : (
                <div>
                  <Label className="lx-label">GL account to debit <span className="text-red-500">*</span></Label>
                  <SearchableSelect className="mt-1" testid="approve-account" value={approveForm.account_code} placeholder="Select expense GL account"
                    onValueChange={(v) => setApproveForm({ ...approveForm, account_code: v })}
                    options={accounts.filter((a) => a.type === "Expense").map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }))} />
                </div>
              )}
              <div>
                <Label className="lx-label">Supplier to credit (optional)</Label>
                <SearchableSelect className="mt-1" testid="approve-supplier" value={approveForm.supplier_id} placeholder="— None —"
                  onValueChange={(v) => setApproveForm({ ...approveForm, supplier_id: v })}
                  options={[{ value: "none", label: "— None — (posts directly to GL on payment)" }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))]} />
                <div className="mt-1 text-[11px] text-gray-500" data-testid="approve-supplier-hint">{approveForm.settlement_method === "Credit" ? (approveForm.supplier_id && approveForm.supplier_id !== "none" ? "Credit → accrues to Trade Payables (2000)." : "No supplier → Credit accrues to Expense Payable (2050).") : "Immediate → no accrual; posts Dr Expense / Cr Bank on payment."}</div>
              </div>
              <div data-testid="approve-settlement">
                <Label className="lx-label">Settlement method <span className="text-red-500">*</span></Label>
                <div className="mt-1 flex gap-2">
                  {["Immediate", "Credit"].map((m) => (
                    <button key={m} type="button" data-testid={`settlement-${m.toLowerCase()}`}
                      onClick={() => setApproveForm({ ...approveForm, settlement_method: m, credit_days: m === "Immediate" ? "" : approveForm.credit_days })}
                      className={`flex-1 rounded-sm border px-3 py-1.5 text-sm transition-colors ${approveForm.settlement_method === m ? "border-[#1B4383] bg-[#1B4383]/10 font-semibold text-[#1B4383]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>{m}</button>
                  ))}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {approveForm.settlement_method === "Credit"
                    ? `Accrues now to ${approveForm.supplier_id && approveForm.supplier_id !== "none" ? "Trade Payables (2000)" : "Expense Payable (2050)"}; cleared when paid.`
                    : "No accrual — posts Dr Expense / Cr Bank directly when paid."}
                </div>
              </div>
              {(approveFor.documents || []).length > 0 && (
                <div>
                  <Label className="lx-label">Attached documents ({approveFor.documents.length}){approveFor.is_bd ? " · trip approval" : ""}</Label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {approveFor.documents.map((d) => (
                      <button key={d.id} type="button" data-testid={`approve-doc-${d.id}`} onClick={() => setPreviewDocId(d.id)}
                        className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-xs ${previewDocId === d.id ? "border-[#1B4383] bg-[#1B4383]/10 text-[#1B4383]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                        <Paperclip size={12} className="shrink-0" /><span className="max-w-[140px] truncate">{d.filename}</span>
                      </button>
                    ))}
                  </div>
                  {(() => {
                    const d = approveFor.documents.find((x) => x.id === previewDocId) || approveFor.documents[0];
                    if (!d) return null;
                    const url = fileUrl(`expenses/${approveFor.id}/documents/${d.id}/file`);
                    const isImg = /image\//i.test(d.content_type || "") || /\.(png|jpe?g|webp|gif|bmp)$/i.test(d.filename || "");
                    return (
                      <div className="mt-2 overflow-hidden rounded-sm border border-gray-200 bg-gray-50" data-testid="approve-doc-preview">
                        {isImg ? (
                          <img src={url} alt={d.filename} className="max-h-64 w-full object-contain" />
                        ) : (
                          <iframe src={url} title={d.filename} className="h-64 w-full" />
                        )}
                        <div className="flex justify-end border-t border-gray-200 bg-white px-2 py-1">
                          <button type="button" className="text-[11px] text-blue-600 hover:underline" data-testid={`approve-doc-open-${d.id}`} onClick={() => openPdf(`expenses/${approveFor.id}/documents/${d.id}/file`)}>Open in new tab ↗</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              <div className="rounded-sm border border-gray-200 p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={approveForm.vat_applicable} data-testid="approve-vat-applicable" onCheckedChange={(v) => setApproveForm({ ...approveForm, vat_applicable: v, vat_amount: v ? Number((Number(approveFor.amount_exclusive || 0) * 0.18).toFixed(2)) : 0 })} />
                  VAT applicable — approver's final say (overrides the request)
                </label>
                {approveForm.vat_applicable && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="lx-label">VAT amount <span className="text-gray-400">· editable</span></Label>
                      <Input type="number" className="mt-1" data-testid="approve-vat-amount" value={approveForm.vat_amount} onChange={(ev) => setApproveForm({ ...approveForm, vat_amount: ev.target.value })} />
                      <div className="mt-0.5 text-[11px] text-gray-400">18% = {fmtNum(Number(approveFor.amount_exclusive || 0) * 0.18)} · adjust for mixed / partly-vatable invoices</div>
                    </div>
                    <div>
                      <Label className="lx-label">Total (inclusive)</Label>
                      <div className="mt-2 lx-num font-semibold text-[#1B4383]" data-testid="approve-vat-total">{curSym(approveFor.currency)} {fmtNum(Number(approveFor.amount_exclusive || 0) + Number(approveForm.vat_amount || 0))}</div>
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-gray-500">Exclusive {curSym(approveFor.currency)} {fmtNum(approveFor.amount_exclusive)} · VAT {fmtNum(approveForm.vat_applicable ? Number(approveForm.vat_amount || 0) : 0)}</div>
              </div>
              <div>
                <Label className={`lx-label ${approveForm.settlement_method === "Immediate" ? "text-gray-300" : ""}`}>Credit Days (supplier terms)</Label>
                <Input type="number" className="mt-1 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400" data-testid="approve-credit-days" disabled={approveForm.settlement_method === "Immediate"} placeholder={approveForm.settlement_method === "Immediate" ? "N/A — paid on approval" : "0 = due on invoice date"} value={approveForm.settlement_method === "Immediate" ? "" : approveForm.credit_days} onChange={(e) => setApproveForm({ ...approveForm, credit_days: e.target.value })} />
              </div>
              {!approveFor.is_bd && (
              <div className="rounded-sm border border-gray-200 p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={approveForm.split_enabled} data-testid="approve-split-toggle" onCheckedChange={(v) => setApproveForm({ ...approveForm, split_enabled: v, splits: v && !approveForm.splits.length ? [{ account_code: approveForm.account_code, amount: approveFor.amount_exclusive }] : approveForm.splits })} />
                  Split the exclusive amount across multiple GL accounts
                </label>
                {approveForm.split_enabled && (
                  <div className="space-y-2">
                    {approveForm.splits.map((s, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`approve-split-${i}`}>
                        <SearchableSelect className="flex-1" testid={`approve-split-account-${i}`} value={s.account_code} placeholder="Account"
                          onValueChange={(v) => setApproveForm((f) => ({ ...f, splits: f.splits.map((x, j) => j === i ? { ...x, account_code: v } : x) }))}
                          options={accounts.filter((a) => a.active !== false).map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }))} />
                        <Input type="number" className="w-32" data-testid={`approve-split-amount-${i}`} value={s.amount} onChange={(e) => setApproveForm((f) => ({ ...f, splits: f.splits.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) }))} />
                        <button type="button" className="text-gray-300 hover:text-red-500" data-testid={`approve-split-remove-${i}`} onClick={() => setApproveForm((f) => ({ ...f, splits: f.splits.filter((_, j) => j !== i) }))}><Trash size={14} /></button>
                      </div>
                    ))}
                    <button type="button" className="text-xs text-[#1B4383] hover:underline" data-testid="approve-split-add" onClick={() => setApproveForm((f) => ({ ...f, splits: [...f.splits, { account_code: "", amount: "" }] }))}>+ Add split line</button>
                    {(() => {
                      const sum = approveForm.splits.reduce((a, s) => a + Number(s.amount || 0), 0);
                      const excl = Number(approveFor.amount_exclusive) || 0;
                      const ok = Math.abs(sum - excl) < 0.01;
                      return <div className={`text-[11px] ${ok ? "text-emerald-600" : "text-red-500"}`} data-testid="approve-split-sum">Split total {fmtNum(sum)} / {fmtNum(excl)} {ok ? "✓" : "— must match exclusive"}</div>;
                    })()}
                  </div>
                )}
              </div>
              )}
              <div>
                <Label className="lx-label">Approval note (optional)</Label>
                <Textarea className="mt-1" rows={2} data-testid="approve-note" placeholder="e.g. Approved per BD budget; verify EFD on payment" value={approveForm.note || ""} onChange={(ev) => setApproveForm({ ...approveForm, note: ev.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveFor(null)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="confirm-approve" onClick={confirmApprove}>Approve &amp; Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!efdFor} onOpenChange={(v) => { if (!v) { setEfdFor(null); setEfdForm({}); } }}>
        <DialogContent className="max-w-md" data-testid="efd-dialog">
          <DialogHeader><DialogTitle className="font-display">EFD Details</DialogTitle></DialogHeader>
          {efdFor && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">{efdFor.description}</div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={efdForm.efd_applicable !== false} data-testid="efd-applicable" onCheckedChange={(v) => setEfdForm({ ...efdForm, efd_applicable: v })} />
                EFD applicable
              </label>
              {efdForm.efd_applicable !== false ? (
                <>
                  <div className="flex items-center justify-between rounded-sm border border-dashed border-[#1B4383]/40 bg-[#E6ECF5]/40 px-3 py-2">
                    <span className="text-sm text-gray-600">Have the EFD receipt? Scan it to auto-fill the code &amp; time.</span>
                    <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-[#1B4383] bg-white px-2.5 py-1 text-xs font-semibold text-[#1B4383] transition-colors hover:bg-[#1B4383] hover:text-white ${efdOcrLoading ? "pointer-events-none opacity-60" : ""}`} data-testid="efd-ocr-upload">
                      {efdOcrLoading ? <CircleNotch size={14} className="animate-spin" /> : <Scan size={14} />}
                      {efdOcrLoading ? "Scanning…" : "Scan Receipt (OCR)"}
                      <input type="file" className="hidden" accept="image/*,application/pdf" disabled={efdOcrLoading} onChange={(e) => { scanEfd(e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                  <div className="flex items-center justify-between rounded-sm border border-gray-200 px-3 py-2">
                    <span className="text-sm text-gray-600">Or just attach the EFD copy{(efdFor.documents || []).length ? ` · ${efdFor.documents.length} attached` : ""}</span>
                    <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100 ${efdOcrLoading ? "pointer-events-none opacity-60" : ""}`} data-testid="efd-attach-upload">
                      <UploadSimple size={14} />Attach copy
                      <input type="file" className="hidden" accept="image/*,application/pdf" disabled={efdOcrLoading} onChange={(e) => { attachEfd(e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                  <div>
                    <Label className="lx-label">EFD Verification Code</Label>
                    <Input className="mt-1" data-testid="efd-code" value={efdForm.efd_code || ""} onChange={(e) => setEfdForm({ ...efdForm, efd_code: e.target.value })} />
                  </div>
                  <div>
                    <Label className="lx-label">EFD Date &amp; Time (24h)</Label>
                    <EfdTimeField withDate testid="efd-time" value={efdForm.efd_time || ""} onChange={(v) => setEfdForm({ ...efdForm, efd_time: v })} />
                  </div>
                </>
              ) : <div className="rounded-sm bg-gray-50 px-3 py-2 text-xs text-gray-500">Marked not applicable — this item won't show an EFD-pending tag.</div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEfdFor(null); setEfdForm({}); }}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-efd" onClick={saveEfd}>Save EFD</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pushFor} onOpenChange={(v) => { if (!v) { setPushFor(null); setPushForm({ note: "", credit_days: "" }); } }}>
        <DialogContent className="max-w-md" data-testid="push-payables-dialog">
          <DialogHeader><DialogTitle className="font-display">Push to Payables (without EFD)</DialogTitle></DialogHeader>
          {pushFor && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">{pushFor.description} · <span className="lx-num font-semibold">{curSym(pushFor.currency)} {fmtNum(pushFor.amount_inclusive)}</span></div>
              <div className="rounded-sm bg-amber-50 px-3 py-2 text-xs text-amber-700">This forces the expense into Payables Due before its EFD is captured. It stays tagged “EFD pending”.</div>
              <div>
                <Label className="lx-label">Reason / note <span className="text-red-500">*</span></Label>
                <Textarea className="mt-1" rows={3} data-testid="push-note" value={pushForm.note} onChange={(e) => setPushForm({ ...pushForm, note: e.target.value })} placeholder="Why is this being pushed without an EFD?" />
              </div>
              <div>
                <Label className="lx-label">Credit Days (optional)</Label>
                <Input type="number" className="mt-1" data-testid="push-credit-days" value={pushForm.credit_days} onChange={(e) => setPushForm({ ...pushForm, credit_days: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPushFor(null); setPushForm({ note: "", credit_days: "" }); }}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="confirm-push" onClick={confirmPush} disabled={!pushForm.note.trim()}>Push to Payables</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={(v) => { if (!v) { setRejectFor(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="reject-expense-dialog">
          <DialogHeader><DialogTitle className="font-display">Reject Expense</DialogTitle></DialogHeader>
          {rejectFor && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">{rejectFor.description} · <span className="lx-num font-semibold">{curSym(rejectFor.currency)} {fmtNum(rejectFor.amount_inclusive)}</span></div>
              <div>
                <Label className="lx-label">Reason for rejection (sent to requestor)</Label>
                <Textarea className="mt-1" rows={3} data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Missing supporting documents; amount exceeds policy…" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectFor(null); setRejectReason(""); }}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-expense" onClick={confirmReject} disabled={!rejectReason.trim()}>Reject & Notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewFor} onOpenChange={(v) => { if (!v) setViewFor(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="view-expense-dialog">
          <DialogHeader><DialogTitle className="font-display">Expense Details</DialogTitle></DialogHeader>
          {viewFor && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <div><span className="lx-label text-gray-400">Description</span><div className="font-medium">{viewFor.description}</div></div>
                <div><span className="lx-label text-gray-400">Status</span><div><span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${statusStyle(viewFor.status)}`}>{viewFor.status}</span></div></div>
                <div><span className="lx-label text-gray-400">Type</span><div>{viewFor.is_bd ? "Business Development" : viewFor.expense_type}</div></div>
                <div><span className="lx-label text-gray-400">Amount</span><div className="lx-num">{curSym(viewFor.currency)} {fmtNum(viewFor.amount_inclusive)} <span className="text-xs text-gray-400">(VAT {fmtNum(viewFor.vat_amount)})</span></div></div>
                <div><span className="lx-label text-gray-400">Requested by</span><div>{viewFor.created_by_name || "—"}</div></div>
                <div><span className="lx-label text-gray-400">Supplier</span><div>{viewFor.supplier_name || "—"}</div></div>
              </div>
              {(viewFor.status === "Approved" || viewFor.status === "Paid") && viewFor.approved_by && (
                <div className="rounded-sm border border-emerald-200 bg-emerald-50/50 p-3" data-testid="approval-audit">
                  <div className="lx-label mb-1 text-emerald-700">Approval</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                    <div><span className="text-gray-400">Approved by:</span> {viewFor.approved_by}{viewFor.approved_at ? ` · ${fmtDate(viewFor.approved_at)}` : ""}</div>
                    <div><span className="text-gray-400">GL debited:</span> {viewFor.approved_account_code ? `${viewFor.approved_account_code} · ${viewFor.approved_account_name || ""}` : (viewFor.account_code || "—")}</div>
                    <div><span className="text-gray-400">Supplier credited:</span> {viewFor.approved_supplier_name || (viewFor.approved_supplier_id ? viewFor.supplier_name : "None — posted directly to GL")}</div>
                    {viewFor.approved_note && <div className="col-span-2" data-testid="approval-note"><span className="text-gray-400">Note:</span> {viewFor.approved_note}</div>}
                  </div>
                </div>
              )}
              {viewFor.is_bd && (
                <div className="rounded-sm border border-amber-200 bg-amber-50/40 p-3">
                  <div className="lx-label mb-1 text-amber-700">Business Development Trip</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                    <div><span className="text-gray-400">Trip:</span> {viewFor.trip_no ? `${viewFor.trip_no} · ` : ""}{viewFor.trip_name || "—"}</div>
                    <div><span className="text-gray-400">Employee:</span> {viewFor.bd_employee_name || "—"}</div>
                    <div><span className="text-gray-400">Dates:</span> {(viewFor.trip_from || "—")} → {(viewFor.trip_to || "—")}</div>
                    <div><span className="text-gray-400">Destination:</span> {viewFor.trip_destination || "—"}</div>
                    <div><span className="text-gray-400">Category:</span> {viewFor.bd_category || "—"}</div>
                  </div>
                </div>
              )}
              <div>
                <div className="lx-label mb-1 text-gray-400">Supporting Documents ({(viewFor.documents || []).length})</div>
                {(viewFor.documents || []).length === 0 ? (
                  <div className="text-xs text-gray-400">No attachments provided by the requestor.</div>
                ) : (
                  <div className="space-y-1">
                    {viewFor.documents.map((d) => (
                      <div key={d.id} data-testid={`view-doc-${d.id}`} className="flex items-center justify-between rounded-sm border border-gray-100 px-2 py-1.5">
                        <span className="truncate">{d.filename}<span className="ml-1 text-[11px] text-gray-400">· {fmtDate(d.uploaded_at)}</span></span>
                        <button className="text-xs text-blue-600 hover:underline" data-testid={`view-doc-open-${d.id}`} onClick={() => openPdf(`expenses/${viewFor.id}/documents/${d.id}/file`)}>View</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {(viewFor.status === "Approved" || viewFor.status === "Paid") && (
                <ReverseActions sourceId={viewFor.id} title="Reverse accounting entry" onDone={load} />
              )}
              {viewFor.reject_reason && (
                <div className="rounded-sm border border-red-200 bg-red-50 p-2 text-xs text-red-700"><span className="font-semibold">Rejection reason:</span> {viewFor.reject_reason}</div>
              )}
              {(viewFor.history || []).length > 0 && (
                <div>
                  <div className="lx-label mb-1 text-gray-400">History</div>
                  <div className="space-y-1">
                    {viewFor.history.map((h, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-gray-600"><span className="rounded-sm bg-gray-100 px-1.5 py-0.5">{h.status}</span><span>{fmtDate(h.at)} · {h.by}{h.note ? ` — ${h.note}` : ""}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setViewFor(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
